import { Dayjs } from 'dayjs';
import {
  fromEnumAsString, toEnumFromString,
} from '../../../../serialization/enum-utils.js';
import { MapUtils } from '../../../../utility/map-utils.js';
import { VersionRange } from '../../../core/version-range.js';
import {
  cloneData, cloneDataOrNull, cloneListOfCloneable, DataModel, DataModelBuilder,
  isSameData, isSameList, SerializationContext, SerializedData,
} from '../../../dto/dto.js';
import { GenericOrderEntry, GenericOrderEntryStatus } from '../generic-order-entry.js';
import { OnlineTransactionDetails } from '../../online-transaction-details.js';
import { OrderSideEffect } from '../order-side-effect.js';
import { BusinessModuleRef } from '../../business-module-ref.js';
import { Price } from '../../price.js';
import { TimeZone } from '../../../../time/timezone.js';
import { ElectronicDeliveryMethod } from '../../electronic-delivery-method.js';
import { Refund } from '../../discounts/refund.js';
import { OfflineTransactionDetails } from '../../offline-transaction-details.js';
import { InvoiceVat } from '../../invoicing/invoice-vat.js';

export class GenericOrderDonationEntry extends GenericOrderEntry {
  static readonly TYPE_ID: string = '800216a80087483fb44c8b26ce94971a';
  static readonly TYPE_NAME: string = 'Donation';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly uniqueId: string,
    public readonly orderId: string,
    public readonly entryGroupId: string | null,
    public readonly note: string | null,
    public readonly status: GenericOrderEntryStatus,
    public readonly creatorId: string,
    public readonly creationDate: Dayjs,
    public readonly businessRef: BusinessModuleRef,
    public readonly onlineTransactionDetails: OnlineTransactionDetails | null,
    public readonly offlineTransactionDetails: OfflineTransactionDetails | null,
    public readonly sideEffects: OrderSideEffect[],
    public readonly donation: Price,
    public readonly cancelledDate: Dayjs | null,
    public readonly cancelledBy: string | null,
    public readonly deliveryMethod: ElectronicDeliveryMethod,
  ) {
    super();
  }

  get $typeId(): string {
    return GenericOrderDonationEntry.TYPE_ID;
  }

  get $typeName(): string {
    return GenericOrderDonationEntry.TYPE_NAME;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, GenericOrderDonationEntry.SCHEMA_VERSION);
  }

  static create(args: {
    uniqueId: string,
    orderId: string,
    entryGroupId: string | null,
    note: string | null,
    status: GenericOrderEntryStatus,
    creatorId: string,
    creationDate: Dayjs,
    businessRef: BusinessModuleRef,
    onlineTransactionDetails: OnlineTransactionDetails | null,
    offlineTransactionDetails: OfflineTransactionDetails | null,
    sideEffects: OrderSideEffect[],
    donation: Price,
    cancelledDate: Dayjs | null,
    cancelledBy: string | null,
    deliveryMethod: ElectronicDeliveryMethod,
  }): GenericOrderDonationEntry {
    return new GenericOrderDonationEntry(
        args.uniqueId,
        args.orderId,
        args.entryGroupId,
        args.note,
        args.status,
        args.creatorId,
        args.creationDate,
        args.businessRef,
        args.onlineTransactionDetails,
        args.offlineTransactionDetails,
        args.sideEffects,
        args.donation,
        args.cancelledDate,
        args.cancelledBy,
        args.deliveryMethod,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): GenericOrderDonationEntry {
    return GenericOrderDonationEntry.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      orderId: MapUtils.getString(map, 'orderId'),
      entryGroupId: MapUtils.getStringOrNull(map, 'entryGroupId'),
      note: MapUtils.getStringOrNull(map, 'note'),
      status: toEnumFromString(MapUtils.getString(map, '$status'), GenericOrderEntryStatus),
      creatorId: MapUtils.getString(map, 'creatorId'),
      creationDate: context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      onlineTransactionDetails: context.fromDataOrNull<OnlineTransactionDetails>(MapUtils.getRecordOrNull(map, 'onlineTransactionDetails')),
      offlineTransactionDetails: context.fromDataOrNull<OfflineTransactionDetails>(MapUtils.getRecordOrNull(map, 'offlineTransactionDetails')),
      sideEffects: MapUtils.getList<OrderSideEffect>(map, 'sideEffects', (value) => context.fromData<OrderSideEffect>(value as SerializedData)),
      donation: Price.decode(MapUtils.getString(map, 'donation')),
      cancelledDate: context.decodeDateOrNull(MapUtils.getIntegerOrNull(map, 'cancelledDate')),
      cancelledBy: MapUtils.getStringOrNull(map, 'cancelledBy'),
      deliveryMethod: toEnumFromString(MapUtils.getString(map, 'deliveryMethod'), ElectronicDeliveryMethod),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'orderId': this.orderId,
      'entryGroupId': this.entryGroupId,
      'note': this.note,
      '$status': fromEnumAsString(this.status),
      'creatorId': this.creatorId,
      'creationDate': context.encodeDate(this.creationDate),
      'onlineTransactionDetails': context.toDataOrNull(this.onlineTransactionDetails),
      'offlineTransactionDetails': context.toDataOrNull(this.offlineTransactionDetails),
      'sideEffects': this.sideEffects.map((e) => context.toData(e)),
      'businessRef': this.businessRef.encode(),
      'donation': this.donation.encode(),
      'cancelledDate': context.encodeDateOrNull(this.cancelledDate),
      'cancelledBy': this.cancelledBy,
      'deliveryMethod': fromEnumAsString(this.deliveryMethod),
    };
  }

  clone(): this {
    return this.copyWith() as this;
  }

  copyWith({
    uniqueId = this.uniqueId,
    orderId = this.orderId,
    entryGroupId = this.entryGroupId,
    note = this.note,
    status = this.status,
    creatorId = this.creatorId,
    creationDate = TimeZone.cloneDate(this.creationDate),
    onlineTransactionDetails = cloneDataOrNull(this.onlineTransactionDetails),
    offlineTransactionDetails = cloneDataOrNull(this.offlineTransactionDetails),
    sideEffects = cloneListOfCloneable(this.sideEffects),
    businessRef = cloneData(this.businessRef),
    donation = cloneData(this.donation),
    cancelledDate = TimeZone.cloneDateOrNull(this.cancelledDate),
    cancelledBy = this.cancelledBy,
    deliveryMethod = this.deliveryMethod,
  }: {
    uniqueId?: string,
    orderId?: string,
    entryGroupId?: string | null,
    note?: string | null,
    status?: GenericOrderEntryStatus,
    creatorId?: string,
    creationDate?: Dayjs,
    businessRef?: BusinessModuleRef,
    onlineTransactionDetails?: OnlineTransactionDetails | null,
    offlineTransactionDetails?: OfflineTransactionDetails | null,
    sideEffects?: OrderSideEffect[],
    donation?: Price,
    cancelledDate?: Dayjs | null,
    cancelledBy?: string | null,
    deliveryMethod?: ElectronicDeliveryMethod,
  } = {}): this {
    return GenericOrderDonationEntry.create({
      uniqueId,
      orderId,
      entryGroupId,
      note,
      status,
      creatorId,
      creationDate,
      businessRef,
      onlineTransactionDetails,
      offlineTransactionDetails,
      sideEffects,
      donation,
      cancelledDate,
      cancelledBy,
      deliveryMethod,
    }) as this;
  }

  copyAsAborted(): this {
    return this.copyWith({
      status: GenericOrderEntryStatus.Aborted,
    });
  }

  copyAsCanceled(cancelledBy: string | null, cancelledDate: Dayjs, isRefund: boolean): this {
    return this.copyWith({
      status: isRefund ? GenericOrderEntryStatus.Refunded : GenericOrderEntryStatus.Canceled,
      cancelledDate: TimeZone.cloneDateOrNull(this.cancelledDate) ?? cancelledDate,
      cancelledBy: this.cancelledBy ?? cancelledBy,
    });
  }

  copyWithOnlineTransactionDetails(transactionDetails: OnlineTransactionDetails): this {
    return this.copyWith({
      onlineTransactionDetails: transactionDetails,
    });
  }

  copyWithOfflineTransactionDetails(transactionDetails: OfflineTransactionDetails): this {
    return this.copyWith({
      offlineTransactionDetails: transactionDetails,
    });
  }

  isSame(other: GenericOrderDonationEntry): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.orderId === this.orderId &&
      other.entryGroupId === this.entryGroupId &&
      other.note === this.note &&
      other.status === this.status &&
      other.creatorId === this.creatorId &&
      other.cancelledDate === this.cancelledDate &&
      other.cancelledBy === this.cancelledBy &&
      other.deliveryMethod === this.deliveryMethod &&
      TimeZone.isSameInstant(other.creationDate, this.creationDate) &&
      isSameData(other.onlineTransactionDetails, this.onlineTransactionDetails) &&
      isSameData(other.offlineTransactionDetails, this.offlineTransactionDetails) &&
      isSameList(other.sideEffects, this.sideEffects, isSameData) &&
      isSameData(other.businessRef, this.businessRef) &&
      isSameData(other.donation, this.donation);
  }

  tryGetApplicationFee(): Price | null {
    return null;
  }

  tryGetApplicationFeeVat(): InvoiceVat | null {
    return null;
  }

  tryGetRefunds(): Refund[] | null {
    return null;
  }

  tryAddRefund(refund: Refund): boolean {
    return false;
  }

  tryGetEventId(): string | null {
    return null;
  }

  static verifyType(entry: DataModel): boolean {
    return entry instanceof GenericOrderDonationEntry;
  }

  static assertType(entry: DataModel): entry is GenericOrderDonationEntry {
    return entry instanceof GenericOrderDonationEntry;
  }
}

export class GenericOrderDonationEntryBuilder extends DataModelBuilder<GenericOrderDonationEntry> {
  build(context: SerializationContext, map: SerializedData): GenericOrderDonationEntry {
    return GenericOrderDonationEntry.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    this.migrateEnum(map, '$status', GenericOrderEntryStatus, GenericOrderEntryStatus.Open);

    if(versionRange.contains(GenericOrderDonationEntry.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(GenericOrderDonationEntry.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In version 2 we added `offlineTransactionDetails` of type `OfflineTransactionDetails | null`.
    if(version < 2) version = 2;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
